import React, { useCallback, useMemo } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, Cell, PieChart, Pie,
} from "recharts";
import { AlertTriangle, Layers, ListChecks, Shield, TrendingUp } from "lucide-react";
import Card from "./Card";
import { asSentence } from "../utils/aiFleetPortfolio";

const KIND_LABELS = {
  Agent: "Agent",
  Application: "AI Application",
  ModelDeployment: "Model Deployment",
  McpServer: "MCP Server",
  Workload: "Workload",
  PersonalUse: "Personal use",
};

const GOVERNED_COLOR = "#16a34a"; // green-600
const SHADOW_COLOR = "#dc2626";   // red-600
const GROWTH_COLOR = "#7e22ce";   // purple-700 (matches the module accent)
const EXPOSURE_COLOR = "#dc2626"; // red-600

// Remediation-state palette. Amber-600 and red-700 rather than the 500/600 steps: the three sit
// adjacent in one stacked bar, and the lighter steps fail the colorblind-separation check there
// (validated). The card-background stroke between segments carries the split when hue cannot.
const REMEDIATION_STATES = [
  { key: "completed", label: "Remediation complete", color: "#16a34a" },  // green-600
  { key: "inProgress", label: "In progress", color: "#d97706" },          // amber-600
  { key: "notStarted", label: "Not started", color: "#b91c1c" },          // red-700
];

// Criticality-tier palette, in the fixed slice order. The badges themselves are Tailwind
// classes (Tier 1 wears purple-300/800, Tier 2/3 the neutral border/content tokens); Recharts
// needs hex, so these are the matching values. Untiered is amber because it is the call to
// action — the work queue (§11j) — not a fourth tier.
export const TIER_SLICES = [
  { key: "tier1", label: "Tier 1", color: "#7e22ce" },      // purple-700 (module accent)
  { key: "tier2", label: "Tier 2", color: "#6b7280" },      // gray-500
  { key: "tier3", label: "Tier 3", color: "#9ca3af" },      // gray-400
  { key: "untiered", label: "Untiered", color: "#d97706" }, // amber-600
];

/**
 * The four tier slices with their counts from the server's byTier (never from grouping the
 * asset list — the list is filtered and paged; the summary is not). Missing or non-numeric
 * counts read as 0 so the legend always carries all four entries.
 */
export const buildTierSlices = (byTier) =>
  TIER_SLICES.map((s) => ({ ...s, value: Math.max(0, Number(byTier?.[s.key]) || 0) }));

const SEVERITY_LABELS = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  OTHER: "Unrated",
};

function formatImpact(val) {
  if (val == null) return "—";
  const n = Number(val);
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

// Lower-bound form for figures shown as values (tooltips), matching the dashboard's "$1.2M+".
// Axis ticks keep the bare form — a "+" on every gridline reads as noise, not as a qualifier.
function formatImpactPlus(val) {
  if (val == null) return "—";
  return Number(val) > 0 ? `${formatImpact(val)}+` : formatImpact(val);
}

function formatDateTick(ts) {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const GovernanceTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border p-2 text-sm shadow-lg rounded">
      <div className="font-semibold">{KIND_LABELS[d.kind] || d.kind}</div>
      <div style={{ color: GOVERNED_COLOR }}>Governed: {d.governed}</div>
      <div style={{ color: SHADOW_COLOR }}>Shadow: {d.shadow}</div>
    </div>
  );
};

const GrowthTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border p-2 text-sm shadow-lg rounded">
      <div className="font-semibold">{new Date(d.ts).toLocaleDateString()}</div>
      <div>{d.count} asset{d.count === 1 ? "" : "s"} discovered</div>
    </div>
  );
};

const RemediationTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const total = d.completed + d.inProgress + d.notStarted;
  return (
    <div className="bg-white border p-2 text-sm shadow-lg rounded">
      <div className="font-semibold">
        {SEVERITY_LABELS[d.severity] || d.severity} — {total} active risk{total === 1 ? "" : "s"}
      </div>
      {REMEDIATION_STATES.map(({ key, label, color }) => (
        <div key={key} style={{ color }}>{label}: {d[key]}</div>
      ))}
    </div>
  );
};

const TierTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border p-2 text-sm shadow-lg rounded">
      <div className="font-semibold" style={{ color: d.color }}>{d.label}</div>
      <div>{d.value} active asset{d.value === 1 ? "" : "s"}</div>
    </div>
  );
};

const ExposureTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border p-2 text-sm shadow-lg rounded">
      <div className="font-semibold">{d.name}</div>
      {d.kind && <div className="text-slate-500">{KIND_LABELS[d.kind] || d.kind}</div>}
      <div>Exposure: {formatImpactPlus(d.exposure)}</div>
      <div>{d.openRiskCount} open risk{d.openRiskCount === 1 ? "" : "s"}</div>
    </div>
  );
};

function ChartCard({ Icon, iconTone, title, badge, children }) {
  return (
    <Card className="p-4">
      <div className="text-sm font-medium text-content-primary mb-2 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconTone}`} /> {title}
        {badge && (
          <span className="ml-auto px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
            {badge}
          </span>
        )}
      </div>
      {children}
    </Card>
  );
}

const EmptyChart = ({ message }) => (
  <div className="h-[180px] flex items-center justify-center text-xs text-content-secondary">
    {message}
  </div>
);

/**
 * The estate's cumulative discovery curve — historical context rather than a decision surface,
 * so it lives in the Overview's collapsed Trends section rather than the always-visible grid.
 */
export function EstateGrowthChart({ summary }) {
  // Extend the discovery curve to today so the line reaches the right edge (and a
  // single-discovery-day estate still draws a visible line).
  const growthData = useMemo(() => {
    const points = (summary?.discoveryTimeline || []).map((p) => ({
      ts: new Date(p.date).getTime(),
      count: p.count,
    }));
    if (points.length > 0) {
      const last = points[points.length - 1];
      const today = new Date().setHours(0, 0, 0, 0);
      if (last.ts < today) points.push({ ts: today, count: last.count });
    }
    return points;
  }, [summary]);

  return (
    <ChartCard
      Icon={TrendingUp}
      iconTone="text-purple-700"
      title="Estate growth"
      badge={summary?.discoveredLast30Days > 0 ? `+${summary.discoveredLast30Days} in 30 days` : null}
    >
      {growthData.length === 0 ? (
        <EmptyChart message="No discoveries recorded yet" />
      ) : (
        <ResponsiveContainer width="100%" height={210}>
          <AreaChart data={growthData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={formatDateTick}
              tick={{ fontSize: 11 }}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={35} />
            <Tooltip content={<GrowthTooltip />} />
            <Area
              type="stepAfter"
              dataKey="count"
              stroke={GROWTH_COLOR}
              fill={GROWTH_COLOR}
              fillOpacity={0.12}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

/**
 * Active assets by criticality tier — a donut whose untiered slice is framed as the work queue
 * (the centre figure is the untiered count while there is one). Renders only from
 * summary.byTier; zero-count tiers stay in the legend with "0" but draw no slice. Any slice
 * (or legend entry) opens the Tiers tab through onSelectTier(tierKey).
 */
export function TierDistributionChart({ summary, onSelectTier }) {
  const slices = useMemo(() => buildTierSlices(summary?.byTier), [summary]);
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const untiered = slices.find((s) => s.key === "untiered")?.value ?? 0;
  const pieData = slices.filter((s) => s.value > 0);

  const handleSelect = useCallback((entry) => {
    const key = entry?.key ?? entry?.payload?.key;
    if (key && onSelectTier) onSelectTier(key);
  }, [onSelectTier]);

  return (
    <ChartCard
      Icon={Layers}
      iconTone="text-purple-700"
      title="Assets by tier"
      badge={untiered > 0 ? `${untiered} to classify` : null}
    >
      {total === 0 ? (
        <EmptyChart message="No active assets yet." />
      ) : (
        <>
          <div className="relative">
            <ResponsiveContainer width="100%" height={180} initialDimension={{ width: 300, height: 180 }}>
              <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={2}
                  // Card-background seam so the two greys stay separable side by side.
                  stroke="var(--bg-card-solid)"
                  strokeWidth={1}
                  isAnimationActive={false}
                  // Recharts 3 assigns each sector's onClick from the Pie, so a per-Cell
                  // onClick never fires; the clicked entry (with its tier key) arrives here.
                  onClick={handleSelect}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} cursor="pointer" data-testid={`tier-slice-${entry.key}`} />
                  ))}
                </Pie>
                <Tooltip content={<TierTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Centre figure: the untiered count is the call to action; once nothing is left to
                classify the ring reports the estate size instead. */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span
                className="text-2xl font-semibold leading-none text-content-primary"
                style={untiered > 0 ? { color: "#d97706" } : undefined}
                data-testid="tier-donut-centre"
              >
                {untiered > 0 ? untiered : total}
              </span>
              <span className="text-[11px] text-content-secondary mt-1">
                {untiered > 0 ? "untiered" : "active assets"}
              </span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-secondary">
            {slices.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => handleSelect(s)}
                className="flex items-center gap-1.5 hover:text-content-primary"
                aria-label={`${s.label}: ${s.value}`}
              >
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: s.color }} />
                {s.label} ({s.value})
              </button>
            ))}
          </div>
          <div className="mt-1 text-xs text-content-secondary">Click a slice to open the Tiers view.</div>
        </>
      )}
    </ChartCard>
  );
}

// Exported for its unit test and for SpendTrajectoryChart's tooltip — the mixed-currency and
// not-reported rules are behavior, not style.
export const formatCostValue = (v, currency) => {
  if (v == null) return "Not reported";
  if (!currency) return `${v.toFixed(2)} (mixed currencies)`;
  return new Intl.NumberFormat(undefined, {
    style: "currency", currency, maximumFractionDigits: 2,
  }).format(v);
};

/**
 * Posture-at-a-glance charts for the Overview tab: governance split per asset kind (the decision
 * view of the Shadow story), top assets by open-risk financial exposure, and remediation
 * progress, and active assets by criticality tier. Renders entirely from the /ai-assets/summary
 * payload — unaffected by the inventory filters. Estate growth lives in
 * <see>EstateGrowthChart</see>, under the Trends expander. When the server withholds the
 * estate-wide exposure from this caller (topExposureAssetsUnavailableReason), the exposure card
 * states the reason instead of drawing an empty chart.
 */
export const NO_POSITIVE_FAIR_EXPOSURE_MESSAGE =
  "No asset has a positive FAIR-aligned expected-loss estimate";

export default function AiIntelligenceCharts({ summary, onAssetClick, onSelectTier }) {
  const governanceData = summary?.governanceByKind || [];
  const exposureData = summary?.topExposureAssets || [];
  const exposureWithheldReason = summary?.topExposureAssetsUnavailableReason;
  // topExposureAssets drops assets with no positive FAIR-aligned exposure, so an empty list beside
  // open findings means "nothing priced", not "nothing open". A withheld list is checked first.
  const exposureEmptyMessage = summary?.openRiskCount > 0
    ? NO_POSITIVE_FAIR_EXPOSURE_MESSAGE
    : "No open AI asset risks";
  const remediationData = summary?.remediationProgress?.bySeverity || [];
  const closedRisks = summary?.remediationProgress?.closedRisks ?? 0;

  const handleExposureClick = useCallback((entry) => {
    const d = entry?.payload ?? entry;
    if (d?.assetId && onAssetClick) onAssetClick(d.assetId, d.isStale);
  }, [onAssetClick]);

  return (
    // Four cards: 2×2 on most screens, one row only where a quarter-width card still leaves the
    // horizontal bar charts room for their 110px category axis.
    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4 mb-6">
      <ChartCard Icon={Shield} iconTone="text-green-600" title="Governance by kind">
        {governanceData.length === 0 ? (
          <EmptyChart message="No active AI assets yet" />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart layout="vertical" data={governanceData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="kind"
                  width={110}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(k) => KIND_LABELS[k] || k}
                />
                <Tooltip content={<GovernanceTooltip />} />
                <Bar dataKey="governed" stackId="gov" fill={GOVERNED_COLOR} barSize={16} />
                <Bar dataKey="shadow" stackId="gov" fill={SHADOW_COLOR} barSize={16} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-2 flex gap-4 text-xs text-content-secondary">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: GOVERNED_COLOR }} /> Governed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: SHADOW_COLOR }} /> Shadow
              </span>
            </div>
          </>
        )}
      </ChartCard>

      <ChartCard Icon={AlertTriangle} iconTone="text-red-600" title="Top assets by exposure">
        {exposureWithheldReason ? (
          <EmptyChart message={asSentence(exposureWithheldReason)} />
        ) : exposureData.length === 0 ? (
          <EmptyChart message={exposureEmptyMessage} />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart layout="vertical" data={exposureData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tickFormatter={formatImpact} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={110}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(n) => (n.length > 16 ? `${n.slice(0, 15)}…` : n)}
                />
                <Tooltip content={<ExposureTooltip />} />
                <Bar dataKey="exposure" barSize={16} radius={[0, 3, 3, 0]} onClick={handleExposureClick} minPointSize={3}>
                  {exposureData.map((entry) => (
                    <Cell key={entry.assetId} fill={EXPOSURE_COLOR} cursor="pointer" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-2 text-xs text-content-secondary">Click a bar to open the asset&apos;s risks below.</div>
          </>
        )}
      </ChartCard>

      <ChartCard
        Icon={ListChecks}
        iconTone="text-green-600"
        title="Remediation progress"
        badge={closedRisks > 0 ? `${closedRisks} risk${closedRisks === 1 ? "" : "s"} closed` : null}
      >
        {remediationData.length === 0 ? (
          <EmptyChart message="No open AI asset risks" />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart layout="vertical" data={remediationData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="severity"
                  width={70}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(s) => SEVERITY_LABELS[s] || s}
                />
                <Tooltip content={<RemediationTooltip />} />
                {REMEDIATION_STATES.map(({ key, color }, i) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    stackId="rem"
                    fill={color}
                    barSize={16}
                    // Card-background seam between segments — the adjacent hues alone are not
                    // separable for every kind of color vision.
                    stroke="var(--bg-card-solid)"
                    strokeWidth={1}
                    radius={i === REMEDIATION_STATES.length - 1 ? [0, 3, 3, 0] : undefined}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-secondary">
              {REMEDIATION_STATES.map(({ key, label, color }) => (
                <span key={key} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: color }} /> {label}
                </span>
              ))}
            </div>
          </>
        )}
      </ChartCard>

      <TierDistributionChart summary={summary} onSelectTier={onSelectTier} />
    </div>
  );
}
